import {
  Add,
  CloudUpload,
  Delete,
  Download,
  FileCopy,
  Folder,
  MoreVert,
  Refresh,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { deleteWorkspaceFile, FileItem, useWorkspaceFiles } from 'api/files';
import { useNotifications } from 'components/notify';
import { useFileUpload } from 'hooks/useFileUpload';
import useIsMobile from 'hooks/useIsMobile';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function FilesPage() {
  const { workspaceId } = useParams();
  const _isMobile = useIsMobile();
  const { data: files, mutate: refreshFiles } = useWorkspaceFiles();
  const { showNotification } = useNotifications();
  const { uploadFile, uploadProgress } = useFileUpload();
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !workspaceId) return;

    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Upload all selected files
    for (let i = 0; i < files.length; i++) {
      await uploadFile(files[i]);
    }
  };

  const handleFileMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    file: FileItem,
  ) => {
    setSelectedFile(file);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleFileMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedFile(null);
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!workspaceId) return;

    try {
      await deleteWorkspaceFile(workspaceId, fileId);
      showNotification('File deleted successfully', {
        variant: 'success',
      });
      handleFileMenuClose();
    } catch (error) {
      console.error('Failed to delete file:', error);
      showNotification('Failed to delete file', {
        variant: 'error',
      });
    }
  };

  const handleCopyLink = (file: FileItem) => {
    const s3Url = file.url;

    window.open(s3Url, '_blank');
    handleFileMenuClose();
  };

  const handleFileUploadFromDrop = useCallback(
    async (file: File) => {
      if (!workspaceId) return;

      // Reset file input to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await uploadFile(file);
    },
    [workspaceId, uploadFile],
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      const droppedFiles = Array.from(event.dataTransfer.files);
      if (droppedFiles.length === 0) return;

      for (const file of droppedFiles) {
        await handleFileUploadFromDrop(file);
      }
    },
    [handleFileUploadFromDrop],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [],
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      // Only set isDragOver to false if we're leaving the main container
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setIsDragOver(false);
      }
    },
    [],
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box
      sx={{
        p: 3,
        border: isDragOver ? '2px dashed' : '2px solid transparent',
        borderColor: isDragOver ? 'primary.main' : 'transparent',
        borderRadius: 2,
        bgcolor: isDragOver ? 'action.hover' : 'transparent',
        transition: 'all 0.2s ease',
        position: 'relative',
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CloudUpload sx={{ fontSize: 64, color: 'primary.main' }} />
          <Typography variant="h4" color="primary.main">
            Drop files here to upload
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Multiple files supported
          </Typography>
        </Box>
      )}
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4" fontWeight="bold">
            Files
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refreshFiles()}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              component="label"
              disabled={uploadProgress.uploading}
            >
              {uploadProgress.uploading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Uploading...
                </>
              ) : (
                'Upload File'
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleFileUpload}
                accept="*/*"
                multiple
              />
            </Button>
          </Stack>
        </Stack>

        <Divider />

        {/* Upload Progress */}
        {uploadProgress.uploading && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">{uploadProgress.status}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress.progress}
                />
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress.progress}% complete
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Files List */}
        <Card>
          <CardContent>
            {!files ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading files...</Typography>
              </Stack>
            ) : files.length === 0 ? (
              <Stack alignItems="center" py={4} spacing={2}>
                <Folder sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  No files uploaded yet
                </Typography>
                <Typography color="text.secondary" textAlign="center">
                  Upload your first file to get started
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  component="label"
                >
                  Upload File
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={handleFileUpload}
                    multiple
                  />
                </Button>
              </Stack>
            ) : (
              <List>
                {files.map((file) => (
                  <ListItem key={file.id} divider>
                    <ListItemIcon>
                      <FileCopy />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatFileSize(+file.size)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(file.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {file.status}
                          </Typography>
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={(e) => handleFileMenuOpen(e, file)}>
                        <MoreVert />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* File Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleFileMenuClose}
      >
        <MenuItem onClick={handleFileMenuClose}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          Download
        </MenuItem>
        <MenuItem onClick={() => selectedFile && handleCopyLink(selectedFile)}>
          <ListItemIcon>
            <FileCopy fontSize="small" />
          </ListItemIcon>
          Copy Link
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => selectedFile && handleDeleteFile(selectedFile.id)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}
